import {
	CheckCircleIcon,
	ClockIcon,
	InfoIcon,
	MinusCircleIcon,
	WarningCircleIcon,
	WarningIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { cn } from "../cn.js";

export type StatusTone = "positive" | "caution" | "critical" | "neutral" | "pending" | "info";

export interface StatusPillProps {
	tone: StatusTone;
	children: ReactNode;
	/** Hides the icon where the surrounding row already carries one. */
	hideIcon?: boolean;
	className?: string;
}

const TONES: Record<StatusTone, { classes: string; Icon: typeof CheckCircleIcon; label: string }> = {
	positive: {
		classes: "bg-(--color-positive-50) text-(--color-positive-700) ring-(--color-positive-500)/20",
		Icon: CheckCircleIcon,
		label: "Healthy",
	},
	caution: {
		classes: "bg-(--color-caution-50) text-(--color-caution-700) ring-(--color-caution-500)/25",
		Icon: WarningIcon,
		label: "Needs attention",
	},
	critical: {
		classes: "bg-(--color-critical-50) text-(--color-critical-700) ring-(--color-critical-500)/20",
		Icon: WarningCircleIcon,
		label: "Problem",
	},
	pending: {
		classes: "bg-(--surface-sunken) text-(--text-secondary) ring-(--border-subtle)",
		Icon: ClockIcon,
		label: "Pending",
	},
	neutral: {
		classes: "bg-(--surface-sunken) text-(--text-secondary) ring-(--border-subtle)",
		Icon: MinusCircleIcon,
		label: "Inactive",
	},
	info: {
		classes: "bg-(--color-brand-50) text-(--color-brand-700) ring-(--color-brand-500)/20",
		Icon: InfoIcon,
		label: "Information",
	},
};

/**
 * Status indicator.
 *
 * Colour is never the only signal: each tone carries a distinct icon and the
 * text label states the status outright (spec §47).
 */
export function StatusPill({ tone, children, hideIcon = false, className }: StatusPillProps) {
	const { classes, Icon, label } = TONES[tone];

	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full px-2 py-0.5",
				"text-xs font-medium ring-1 ring-inset",
				classes,
				className,
			)}
		>
			{!hideIcon && <Icon className="size-3.5 shrink-0" weight="fill" aria-hidden="true" />}
			<span className="sr-only">{label}: </span>
			{children}
		</span>
	);
}
